import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PartOrderLine } from '@techgitter/contracts';
import { fetchPartOrderLines } from '../services/partOrdersApi';
import { useSignalREvent } from './useSignalR';

/**
 * Single page-level fetch of every non-cancelled part-order line, grouped by
 * `workitemId`. Used by the workitem dashboard to render PartsReadyBadge on
 * each row without firing one HTTP call per row.
 *
 * Auto-refreshes on the three parts:* SignalR events so badge state stays
 * live while the dashboard is open.
 *
 * Returns an empty map until the first fetch completes; callers should not
 * conditionally hide the dashboard on it.
 */
export function usePartOrderLinesByWorkitem(): {
  linesByWorkitem: Record<string, PartOrderLine[]>;
  refresh: () => void;
} {
  const [lines, setLines] = useState<PartOrderLine[]>([]);

  const refresh = useCallback(async () => {
    try {
      const items = await fetchPartOrderLines({});
      // We don't gate on status server-side because the badge needs visibility
      // of received lines to render the "Parts ready" terminal state. The
      // backend list endpoint is cheap enough at Phase 1 volume (~tens of
      // lines) to fetch the lot.
      setLines(items);
    } catch {
      // Swallow — badge is non-essential; failing silently is fine.
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useSignalREvent('parts:to_order', () => refresh());
  useSignalREvent('parts:order_placed', () => refresh());
  useSignalREvent('parts:line_received', () => refresh());

  const linesByWorkitem = useMemo(() => {
    const map: Record<string, PartOrderLine[]> = {};
    for (const line of lines) {
      if (!line.workitemId) continue;
      const bucket = map[line.workitemId] || [];
      bucket.push(line);
      map[line.workitemId] = bucket;
    }
    return map;
  }, [lines]);

  return { linesByWorkitem, refresh };
}
